/** One open position, as far as exposure is concerned. */
export interface ExposurePosition {
  symbol: string;
  side: string;
  volume: number;
  priceOpen: number;
}

/** What exposure needs to know of a symbol. */
export interface ExposureSymbol {
  contractSize: number;
  currencyBase: string;
  currencyProfit: string;
}

export interface ExposureRow {
  asset: string;
  /** Units of the asset held: positive long, negative short. */
  volume: number;
  /** Positions that make it up. */
  positions: number;
}

/**
 * The account's net exposure by asset, as the Exposure tab of the Toolbox shows it: a buy of 1 lot EURUSD at 1.10 is
 * +100 000 EUR and -110 000 USD. A symbol quoted in its own currency (an index or a commodity CFD) is its own asset.
 * Assets that net to zero are left out; the largest exposure comes first.
 */
export function exposureOf(
  positions: readonly ExposurePosition[],
  symbolOf: (name: string) => ExposureSymbol | undefined,
): ExposureRow[] {
  const rows = new Map<string, ExposureRow>();
  const add = (asset: string, amount: number) => {
    const row = rows.get(asset) ?? { asset, volume: 0, positions: 0 };
    row.volume += amount;
    row.positions += 1;
    rows.set(asset, row);
  };
  for (const p of positions) {
    const info = symbolOf(p.symbol);
    if (!info) continue;
    const sign = p.side === 'Sell' ? -1 : 1;
    const units = p.volume * (info.contractSize || 1);
    const own = !info.currencyBase || info.currencyBase === info.currencyProfit;
    add(own ? p.symbol : info.currencyBase, sign * units);
    if (info.currencyProfit) add(info.currencyProfit, -sign * units * p.priceOpen);
  }
  return [...rows.values()]
    .map((r) => ({ ...r, volume: Math.round(r.volume * 100) / 100 }))
    .filter((r) => r.volume !== 0)
    .sort((a, b) => Math.abs(b.volume) - Math.abs(a.volume));
}
